import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "../i18n";

export default function CustomDropdown({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  disabled = false,
  searchable = false,
  searchPlaceholder,
  emptyMessage,
  className = "",
  renderOptionLabel,
}) {
  const { language, t, text } = useTranslation();
  const resolvedSearchPlaceholder = searchPlaceholder || t("ui.searchCurrencies");
  const resolvedEmptyMessage = emptyMessage || t("ui.noCurrencies");
  const displayLocale = language === "fr" ? "fr-FR" : language === "pt-PT" ? "pt-PT" : "en-US";
  const displayCountry = (code) => {
    if (!code) return "";
    try {
      return new Intl.DisplayNames([displayLocale], { type: "region" }).of(code) || code;
    } catch {
      return code;
    }
  };
  const displayCurrency = (code) => {
    if (!code) return "";
    try {
      return new Intl.DisplayNames([displayLocale], { type: "currency" }).of(code) || "";
    } catch {
      return "";
    }
  };
  const optionLabel = (option) =>
    option?.displayCountry && option.countryCode
      ? displayCountry(option.countryCode) + " \u2014 " + (option.symbol || "")
      : text(option?.label || "");
  const optionDescription = (option) =>
    option?.code ? displayCurrency(option.code) || text(option.description) : text(option?.description || "");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectedOption = options.find(
    (option) => String(option.value) === String(value),
  );
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!searchable || !query) return options;
    return options.filter((option) =>
      String(option.searchText || option.label || "")
        .toLowerCase()
        .includes(query),
    );
  }, [options, searchable, search]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const chooseOption = (option) => {
    onChange(option.value);
    setOpen(false);
    setSearch("");
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className={`product-custom-dropdown ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className="product-custom-dropdown-trigger"
        aria-label={text(ariaLabel)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="product-custom-dropdown-value">
          {selectedOption
            ? renderOptionLabel
              ? renderOptionLabel(selectedOption)
              : text(selectedOption.selectedLabel || optionLabel(selectedOption))
            : text(placeholder || "")}
        </span>
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
        </svg>
      </button>
      {open && (
        <div className="product-custom-dropdown-menu">
          {searchable && (
            <div className="product-custom-dropdown-search-wrap">
              <input
                type="search"
                className="product-custom-dropdown-search"
                value={search}
                placeholder={text(resolvedSearchPlaceholder)}
                aria-label={text(resolvedSearchPlaceholder)}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          )}
        <div className="product-custom-dropdown-options" role="listbox" aria-label={text(ariaLabel)}>
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={String(option.value) === String(value)}
                  className={
                    "product-custom-dropdown-option" +
                    (String(option.value) === String(value) ? " is-selected" : "")
                  }
                  key={option.value}
                  onClick={() => chooseOption(option)}
                >
                  <span>{renderOptionLabel ? renderOptionLabel(option) : optionLabel(option)}</span>
                  {option.description && <small>{optionDescription(option)}</small>}
                </button>
              ))
            ) : (
              <div className="product-custom-dropdown-empty">{text(resolvedEmptyMessage)}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
